import { Formik } from "formik";
import { isEqual } from "lodash";
import find from "lodash/find";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchPayPeriods,
  updatePayPeriod,
} from "../../../../../../actions/recruiters/payPeriodActions";
import { invalidatePayPeriodsTable } from "../../../../../../actions/shared/payPeriodActions";
import { clearModal } from "../../../../../../actions/shared/uiActions";
import { makeGetFilteredContracts } from "../../../../../../selectors/contractSelector";
import { makeGetFilteredPayPeriods } from "../../../../../../selectors/payPeriodSelectors";
import { makeGetCandidate } from "../../../../../../selectors/recruiter/candidateSelectors";
import { mapKeysToSnakeCase } from "../../../../../../util/formatHelpers";
import { getFileValueAttributes } from "../../../../../../util/storageHelpers";
import Button from "../../../../../forms/custom/Button";
import ActionModalContainer from "../../../../../General/ActionModal/ActionModalContainer";
import ActionModalContent from "../../../../../General/ActionModal/ActionModalContent";
import ActionModalFooter from "../../../../../General/ActionModal/ActionModalFooter";
import ActionModalHeader from "../../../../../General/ActionModal/ActionModalHeader";
import useSnackbar from "../../../../../General/useSnackbar";

import PayPeriodForm from "./PayPeriodForm";
import { buildInitialValues, validationSchema, allocatableValidationSchema, transformValues } from "./utils";

function formatDate(date) {
  return moment.parseZone(date).format("ddd. MMM D, YYYY");
}

export default function SubmitHoursModal({
  candidateId,
  setSubmitted = () => { },
  title = "Add Hours Worked",
  initialPayPeriodId,
}) {
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();
  const [edit, setEdit] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const [selectedPayPeriod, setSelectedPayPeriod] = useState();
  const [payPeriodOptions, setPayPeriodOptions] = useState();

  const getCandidate = useMemo(makeGetCandidate, []);
  const candidate = useSelector(
    (state) => getCandidate(state, candidateId),
    (previousValue, currentValue) => previousValue.id === currentValue.id,
  );

  const getContracts = useMemo(makeGetFilteredContracts, []);
  const contract = useSelector(
    (state) => getContracts(state, { candidateId })[0],
    isEqual,
  );

  const getPayPeriods = useMemo(makeGetFilteredPayPeriods, []);
  const hasEmployerSubmittedTimesheets =
    contract?.attributes?.hasEmployerSubmittedTimesheets === true;
  const payPeriods = useSelector(
    (state) =>
      orderBy(
        getPayPeriods(state, {
          contractId: contract?.id,
          agencySubmittable: !hasEmployerSubmittedTimesheets,
        }),
        (payPeriod) => payPeriod.attributes.startDate,
      ).reverse(),
    isEqual,
  );

  const payPeriod = useSelector(
    (state) => find(getPayPeriods(state), { id: selectedPayPeriod?.value }),
    isEqual,
  );

  // Recruiter is "supplier" — show allocation form when allocatable and recruiter is the allocator
  const showAllocationForm = useMemo(() => {
    return payPeriod?.attributes?.allocatable && payPeriod?.attributes?.projectAllocationParty === "supplier";
  }, [payPeriod?.attributes?.allocatable, payPeriod?.attributes?.projectAllocationParty]);

  const activeValidationSchema = useMemo(() => {
    return showAllocationForm ? allocatableValidationSchema : validationSchema;
  }, [showAllocationForm]);

  const initialValues = useMemo(() => {
    if (payPeriod) {
      return buildInitialValues(payPeriod, showAllocationForm, contract?.attributes?.allocations);
    }
    return null;
  }, [payPeriod, showAllocationForm, contract?.attributes?.allocations]);

  useEffect(() => {
    dispatch(fetchPayPeriods({ candidateId }));
  }, [dispatch, candidateId]);

  useEffect(() => {
    const options = map(payPeriods, (payPeriod) => ({
      value: payPeriod.id,
      label: `${formatDate(payPeriod.attributes.startDate)} - ${formatDate(
        payPeriod.attributes.endDate,
      )}`,
    }));
    setPayPeriodOptions(options);

    let periodIndex = 0;
    if (initialPayPeriodId) {
      const idx = options.findIndex(
        ({ value }) => String(value) === String(initialPayPeriodId),
      );
      periodIndex = idx >= 0 ? idx : 0;
    }
    setSelectedPayPeriod(options[periodIndex] ?? null);
  }, [contract?.id, initialPayPeriodId, payPeriods.length]);

  const isInvoiced = useMemo(
    () => payPeriod?.attributes?.invoiceFinalizedAt != null,
    [payPeriod?.attributes?.invoiceFinalizedAt]
  );

  // Non-beta employers keep the pre-beta lock: once a timesheet is approved it
  // can no longer be edited (invoiced ⟹ approved, so this covers both). Only the
  // beta correction flow keys off isInvoiced. Without this, gating the lock on
  // isInvoiced moved the lock from approved → invoiced, letting non-beta
  // recruiters edit approved hours.
  const isApproved = useMemo(
    () => payPeriod?.attributes?.approvedAt != null,
    [payPeriod?.attributes?.approvedAt]
  );

  const employerInBetaPool = useMemo(
    () =>
      Boolean(
        payPeriod?.attributes?.employerInBetaPool ??
        payPeriod?.attributes?.employer_in_beta_pool ??
        contract?.attributes?.employerInBetaPool
      ),
    [
      payPeriod?.attributes?.employerInBetaPool,
      payPeriod?.attributes?.employer_in_beta_pool,
      contract?.attributes?.employerInBetaPool,
    ]
  );

  const canShowCorrectionFlow = isInvoiced && employerInBetaPool;

  const hasPendingAdjustment = useMemo(
    () => {
      const attrValue = payPeriod?.attributes?.hasPendingAdjustment === true;
      const statusHasCorrection = payPeriod?.attributes?.timesheetStatusWithInvoiceDate?.includes("Correction Submitted");
      return attrValue || statusHasCorrection;
    },
    [payPeriod?.attributes?.hasPendingAdjustment, payPeriod?.attributes?.timesheetStatusWithInvoiceDate]
  );

  useEffect(() => {
    if (payPeriod?.attributes) {
      const shouldEdit =
        !payPeriod.attributes.submittedAt &&
        !payPeriod.attributes.disputedAt &&
        !hasPendingAdjustment;
      setEdit(shouldEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriod?.id]);

  useEffect(() => {
    setSubmissionError("");
  }, [selectedPayPeriod?.value]);

  const submit = async (values) => {
    setSubmissionError("");

    let transformedValues = transformValues(values);

    if (values.document && values.document.lastModified) {
      const documentAttributes = await getFileValueAttributes(
        values.document,
        "document",
      );
      transformedValues = { ...transformedValues, ...documentAttributes };
    }

    // Extract allocations before snake_case conversion to preserve allocation code keys
    const allocations = transformedValues.allocations;
    delete transformedValues.allocations;

    const snakeCasedValues = mapKeysToSnakeCase(transformedValues);

    if (allocations) {
      snakeCasedValues.allocations = allocations;
    }

    return dispatch(
      updatePayPeriod(selectedPayPeriod.value, snakeCasedValues),
    )
      .then(() => {
        const message = canShowCorrectionFlow
          ? "Timesheet correction has been submitted to the employer for review."
          : !!payPeriod.attributes.submittedAt
            ? "Changes to hours have been saved"
            : `Hours submitted for ${selectedPayPeriod.label}`;
        showMessage(message);
        if (canShowCorrectionFlow) {
          dispatch(invalidatePayPeriodsTable());
        }
        dispatch(clearModal());
        setSubmitted(true);
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          setSubmissionError(
            "Hours have already been approved.\nPlease refresh the page.",
          );
        } else {
          showMessage("An error occurred, try again or contact support.");
        }
      });
  };

  const customButtons = useCallback(
    ({ handleSubmit, isSubmitting, payPeriod, touched, resetForm, values }) => {
      const payloadChanged = !isEqual(
        transformValues(values),
        transformValues(initialValues)
      );
      const documentChanged = !isEqual(values.document, initialValues.document);
      const valuesChanged = payloadChanged || documentChanged;

      // When pay period has not been submitted (not invoiced)
      if (
        !payPeriod.attributes.submittedAt &&
        !payPeriod.attributes.disputedAt &&
        !isInvoiced
      ) {
        return (
          <Button
            color="blue"
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-cy="action-modal-button-submit"
          >
            Submit for Approval
          </Button>
        );
      }
      const editHoursDisabled =
        (isApproved && !employerInBetaPool) || hasPendingAdjustment;

      if (!edit) {
        return (
          <Button
            color="blue"
            variant="secondary"
            onClick={() => setEdit(true)}
            disabled={editHoursDisabled}
            data-cy="action-modal-button-edit"
          >
            Edit Hours
          </Button>
        );
      }
      return (
        <div>
          <Button
            color="blue"
            variant="secondary"
            onClick={() => {
              setEdit(false);
              setSubmissionError("");
              resetForm();
            }}
            data-cy="action-modal-button-cancel"
            style={{ marginRight: 12 }}
          >
            Cancel
          </Button>
          <Button
            color={isInvoiced ? "red" : "blue"}
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !valuesChanged}
            data-cy="action-modal-button-submit"
          >
            {isInvoiced ? "Submit Correction" : "Save"}
          </Button>
        </div>
      );
    },
    [edit, isInvoiced, isApproved, canShowCorrectionFlow, employerInBetaPool, initialValues, hasPendingAdjustment]
  );

  if (
    !payPeriod ||
    !initialValues
  ) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={activeValidationSchema}
      onSubmit={submit}
      enableReinitialize
    >
      {({
        submitCount,
        handleSubmit,
        isSubmitting,
        errors,
        touched,
        resetForm,
        values,
      }) => {
        return (
          <ActionModalContainer
            color="blue"
            DialogProps={{ disableEscapeKeyDown: true }}
          >
            <ActionModalHeader
              title={title}
              subtitle={`${candidate?.attributes?.firstName} ${candidate?.attributes?.lastName}`}
            />
            <ActionModalContent>
              {isInvoiced && !employerInBetaPool ? (
                <p>This timesheet has been invoiced. Corrections are not available for this client.</p>
              ) : canShowCorrectionFlow ? (
                <>
                  <p>
                    This timesheet has already been invoiced, so editing the
                    hours will create a Timesheet Correction. Reporting a
                    correction allows you to make adjustments to timesheets that
                    have already been invoiced.
                  </p>
                  <p>
                    When a correction is submitted, it is sent to the employer
                    for approval. Approved corrections will result in an
                    addition or credit on the next invoice.
                  </p>
                </>
              ) : (
                <p>
                  Use this form to submit hours worked. These hours will be
                  presented to the timecard approver. Once approved, an invoice
                  will be sent to the client.
                </p>
              )}
              <PayPeriodForm
                {...{
                  candidate,
                  payPeriod,
                  selectedPayPeriod,
                  setSelectedPayPeriod,
                  payPeriodOptions,
                  edit:
                    (isApproved && !employerInBetaPool)
                      ? false
                      : edit && !hasPendingAdjustment,
                  isInvoiced: canShowCorrectionFlow,
                  showAllocationForm,
                  contractAllocations: contract?.attributes?.allocations,
                }}
              />
            </ActionModalContent>
            <ActionModalFooter
              customButtons={customButtons({
                isSubmitting,
                handleSubmit,
                payPeriod,
                touched,
                resetForm,
                values,
              })}
              submissionError={
                Boolean(submissionError) ||
                (submitCount > 0 && Object.keys(errors).length > 0)
              }
              customErrorMessage={submissionError}
              isSubmitting={isSubmitting}
              overrideErrorStyles={{ width: 400 }}
            />
          </ActionModalContainer>
        );
      }}
    </Formik>
  );
}
